import React, { useContext, useReducer, useRef, useState } from "react";
import { signOut } from "supertokens-auth-react/recipe/session";
import { PopupContentContext } from "../../../context/PopupContentContext";
import { Spinner } from "../../../useOss";
import { InputErrorMessage } from "./InputErrorMessage";
import { UserInfoResponse } from "../../../api/user/info";
import { ChangePasswordModal } from "../../../components/modals/settings-modals/ChangePasswordModal";
import { assertNever, verifyFullNameInput } from "../utils";
import updateUserInfo from "../../../api/user/put";
import { sendButtonAnalytics, sendGenericAnalyticsEvent, sendUserSettingsAnalytics } from "../../../utils";
import { DeleteOwnAccountModal } from "../../../components/modals/settings-modals/DeleteOwnAccountModal";
import { OrganizationInfoResponse } from "../../../api/organization/get";

function ArrowRightIcon(): JSX.Element {
    return (
        <svg width="13" height="11" viewBox="0 0 13 11" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M1.64818 6.69299H1.63019L9.34619 6.69299L6.92119 9.12099C6.80199 9.24042 6.73504 9.40226 6.73504 9.57099C6.73504 9.73973 6.80199 9.90157 6.92119 10.021L7.29819 10.394C7.41646 10.5119 7.57667 10.5781 7.74369 10.5781C7.9107 10.5781 8.07091 10.5119 8.18919 10.394L12.3472 6.23699C12.4648 6.11795 12.5308 5.95734 12.5308 5.78999C12.5308 5.62264 12.4648 5.46204 12.3472 5.34299L8.18919 1.18399C8.07078 1.06632 7.91062 1.00027 7.74369 1.00027C7.57675 1.00027 7.41659 1.06632 7.29819 1.18399L6.92119 1.56199C6.86261 1.62031 6.81619 1.68968 6.78461 1.76606C6.75302 1.84245 6.73691 1.92434 6.73719 2.00699C6.73691 2.08846 6.75306 2.16915 6.78467 2.24424C6.81629 2.31932 6.86272 2.38726 6.92119 2.44399L9.37419 4.88899L1.64019 4.88899C1.47029 4.89237 1.30841 4.96184 1.18891 5.08264C1.06941 5.20345 1.00171 5.36608 1.00019 5.53599V6.06999C1.0049 6.23808 1.07546 6.39758 1.19668 6.51412C1.31789 6.63065 1.48005 6.6949 1.64818 6.69299Z"
                fill="#007AFF"
                stroke="white"
                strokeWidth="0.5"
            />
        </svg>
    );
}

type UserInfoFormState = {
    values: {
        email: string;
        name: string;
    };
    errors: {
        email: string | null;
        name: string | null;
    };
    status: "IDLE" | "EDITING" | "SAVING" | "DELETING";
    hasError: boolean;
};

function getInitialState(user: UserInfoResponse): UserInfoFormState {
    return {
        values: {
            email: user.email,
            name: user.name
        },
        errors: {
            email: null,
            name: null
        },
        status: "IDLE",
        hasError: false
    };
}

type UserInfoFormAction =
    | {
          type: "update_field";
          payload: {
              field: "email" | "name";
              value: string;
              error: string | null;
          };
      }
    | {
          type: "update_status";
          payload: UserInfoFormState["status"];
      };

function userInfoFormReducer(state: UserInfoFormState, action: UserInfoFormAction): UserInfoFormState {
    const hasError = (state: UserInfoFormState) => {
        return Object.values(state.errors).some(val => !!val);
    };
    switch (action.type) {
        case "update_field": {
            const { field, value, error } = action.payload;
            const newState = {
                ...state,
                values: {
                    ...state.values,
                    [field]: value
                },
                errors: {
                    ...state.errors,
                    [field]: error
                }
            };
            return {
                ...newState,
                hasError: hasError(newState)
            };
        }
        case "update_status": {
            return {
                ...state,
                status: action.payload
            };
        }
        default:
            assertNever(action);
            return state;
    }
}
export function UserInfo({
    user,
    onUserUpdate,
    org
}: {
    user: UserInfoResponse;
    onUserUpdate: (user: UserInfoResponse) => void;
    org: OrganizationInfoResponse;
}) {
    const { name, loginMethod } = user;
    const [formState, dispatch] = useReducer(userInfoFormReducer, getInitialState(user));
    const [modalState, setModalState] = useState<"DELETE_ACCOUNT" | "CHANGE_PASSWORD" | "IDLE">("IDLE");
    const { showToast } = useContext(PopupContentContext);

    const nameInputRef = useRef<HTMLInputElement>(null);

    const isEditing = formState.status === "EDITING";
    const isSaveButtonDisabled = formState.hasError || name === formState.values.name;

    async function handleUserInfoUpdate() {
        try {
            dispatch({ type: "update_status", payload: "SAVING" });
            await updateUserInfo(formState.values.name);
            onUserUpdate({
                ...user,
                name: formState.values.name
            });
            sendUserSettingsAnalytics("name_updated", {
                userId: user.userId,
                email: user.email
            });
            dispatch({ type: "update_status", payload: "IDLE" });
            showToast({
                status: "success",
                icon: `/static/assets/dashboard/check-rounded-green.png`,
                children: <>Name updated</>
            });
        } catch (e) {
            showToast({
                status: "error",
                icon: `/static/assets/dashboard/red-issue.png`,
                children: <>Name update failed</>
            });
            dispatch({ type: "update_status", payload: "EDITING" });
        }
    }
    function switchToEditMode() {
        dispatch({ type: "update_status", payload: "EDITING" });
        sendButtonAnalytics("button_user_name_edit");
        setTimeout(() => {
            nameInputRef && nameInputRef.current && nameInputRef.current.focus();
        }, 0);
    }

    function renderEditCTA() {
        switch (formState.status) {
            case "EDITING":
                return (
                    <button
                        className="save-button centered"
                        onClick={handleUserInfoUpdate}
                        disabled={isSaveButtonDisabled}
                    >
                        Save
                    </button>
                );
            case "IDLE":
                return (
                    <div className="edit-item centered" onClick={switchToEditMode}>
                        <img src="/static/assets/dashboard/edit-icon.svg" alt="edit" />
                        <span>Edit</span>
                    </div>
                );
            case "SAVING":
                return (
                    <button className="save-button centered loading" disabled>
                        <Spinner isInline />
                    </button>
                );
        }
    }

    function renderDeleteCTA() {
        return (
            <button
                className="modal-button red-button"
                style={{
                    width: "140px",
                    height: "37px",
                    fontSize: "14px"
                }}
                onClick={() => setModalState("DELETE_ACCOUNT")}
                id="delete-account-button"
            >
                Delete Account
            </button>
        );
    }

    function getProviderImgSource() {
        if (loginMethod.type !== "thirdparty") return "";
        switch (loginMethod.provider) {
            case "google":
                return "/static/assets/dashboard/google.png";
            case "github":
                return "/static/assets/dashboard/github.png";

            default:
                "";
        }
    }

    async function onAccountDeleted() {
        try {
            await signOut();
            sendGenericAnalyticsEvent("signout");
        } catch (e) {
            sendGenericAnalyticsEvent("signout_error");
        }
        window.location.replace("/");
    }

    function renderModal() {
        switch (modalState) {
            case "CHANGE_PASSWORD":
                return <ChangePasswordModal isModalOpen={true} closeModal={() => setModalState("IDLE")} user={user} />;
            case "DELETE_ACCOUNT":
                return (
                    <DeleteOwnAccountModal
                        isModalOpen={true}
                        closeModal={() => setModalState("IDLE")}
                        onAccountDeleted={onAccountDeleted}
                        userRole={!org.organizationFound ? "owner" : org.role}
                        user={user}
                    />
                );
            case "IDLE":
                return null;
        }
    }
    function renderAuthCredentials() {
        const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const newName = e.target.value;
            const result = verifyFullNameInput(newName);
            dispatch({
                type: "update_field",
                payload: {
                    field: "name",
                    value: newName,
                    error: result.success === true ? null : result.reason
                }
            });
        };
        switch (loginMethod.type) {
            case "emailpassword":
                return (
                    <>
                        <div id="user-info">
                            <div>
                                <span>Name</span>
                                <input
                                    type="text"
                                    disabled={!isEditing}
                                    ref={nameInputRef}
                                    value={formState.values.name}
                                    onChange={handleNameChange}
                                    className={`${!!formState.errors.name && "error"}`}
                                />
                                <InputErrorMessage errorMessage={formState.errors.name} />
                            </div>

                            <div>
                                <span>Email</span>
                                <input type="text" disabled value={formState.values.email} className="disabled" />
                            </div>
                        </div>
                        <div id="change-password-wrapper" onClick={() => setModalState("CHANGE_PASSWORD")}>
                            <hr />
                            <div>
                                <span>Change password</span> <ArrowRightIcon />
                            </div>
                        </div>
                    </>
                );

            case "thirdparty":
                return (
                    <div id="user-info">
                        <div>
                            <span>Name</span>
                            <input
                                type="text"
                                disabled={!isEditing}
                                ref={nameInputRef}
                                value={formState.values.name}
                                onChange={handleNameChange}
                                className={`${!!formState.errors.name && "error"}`}
                            />
                            <InputErrorMessage errorMessage={formState.errors.name} />
                        </div>
                        <div className="social-provider-container">
                            <span>Provider</span>
                            <img src={getProviderImgSource()} alt={loginMethod.provider} />
                        </div>
                    </div>
                );
            default:
                assertNever(loginMethod);
        }
    }
    return (
        <>
            <div className="centered-box padded-y user-info-container">
                <div className="separated-flex padded-x " style={{ minHeight: "37px" }}>
                    <span className="heading">User Info</span>
                    <div
                        style={{
                            display: "flex",
                            gap: "40px",
                            justifyContent: "center"
                        }}
                    >
                        {renderEditCTA()}
                        {renderDeleteCTA()}
                    </div>
                </div>
                <hr />
                <div className="padded-x">{renderAuthCredentials()}</div>
                {renderModal()}
            </div>
            <div className="component-spacing"></div>
        </>
    );
}
